var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Point, Numbers, Matrix2D } from '@pacem/pacem-foundation';
import { Geometry, Mathematics } from '@pacem/pacem-numerical';
import { CustomElementUtils, Utils, PCSS, Transformer, Animations, avoidHandler } from '@pacem/pacem-core';
const TWO_PI = Math.PI * 2;
const HALF_PI = Math.PI * .5;
const GET_VAL = CustomElementUtils.getAttachedPropertyValue;
const SET_VAL = CustomElementUtils.setAttachedPropertyValue;
const INITIAL_INNER_ROTATION = 'pacem:2d:circular-slide-rule:inner';
const INITIAL_OUTER_ROTATION = 'pacem:2d:circular-slide-rule:outer';
const HIT_TARGET_ROTATION = 'pacem:2d:circular-slide-rule:target';
export const CIRCULAR_SLIDE_MAX_ZOOM = Math.pow(10, 12);
export const OUTER_GEAR_TAG_ID = 'OUTER', INNER_GEAR_TAG_ID = 'INNER';
export const CIRCULAR_SLIDE_RULER_METADATA = {
    display: { cssClass: [PCSS + '-grid', 'grid-nospace', 'grid-small-rowgap', 'form-minimal'] },
    props: [
        {
            prop: 'size', type: 'int', display: { name: 'stage size', cssClass: [PCSS + '-cell', 'hide-sx', 'hide-v', 'field-stretch', 'field-minimal'], ui: 'slider' },
            validators: [
                { type: 'range', errorMessage: 'out of range', params: { min: 200, max: 800 } }
            ]
        },
        {
            prop: 'base', type: 'number', dataType: 'enumeration', display: { name: 'base (well-known)', cssClass: [PCSS + '-cell', 'hide-sx', 'hide-v', 'field-stretch', 'field-minimal'] },
            extra: { enum: [{ value: 2, caption: '2' }, { value: Math.E, caption: 'e' }, { value: Math.PI, caption: 'π' }, { value: 10, caption: 10 }, { value: 16, caption: 16 }] }
        },
        {
            prop: 'base', type: 'number', display: { name: 'base (custom)', cssClass: [PCSS + '-cell', 'field-stretch', 'field-minimal', 'hide-sx', 'hide-v'], ui: 'slider' },
            extra: { step: .1 },
            validators: [
                { type: 'range', errorMessage: 'out of range', params: { min: 2, max: 36 } }
            ]
        },
        {
            prop: 'webgl', type: 'boolean', display: { name: 'Canvas rendering', cssClass: [PCSS + '-cell', 'field-outline', PCSS + '-switcher', 'switcher-accent'], ui: 'switcher', watermark: 'WebGL rending' }
        }
    ]
};
export function getCircularSlideRulerMetadata(minimal) {
    if (!minimal) {
        return CIRCULAR_SLIDE_RULER_METADATA;
    }
    const retval = Utils.clone(CIRCULAR_SLIDE_RULER_METADATA);
    for (let j = retval.props.length - 1; j >= 0; j--) {
        const prop = retval.props[j];
        if (typeof prop.display.cssClass === 'function') {
            continue;
        }
        if (prop.display.cssClass.indexOf('hide-sx') >= 0) {
            retval.props.splice(j, 1);
        }
        do {
            const ndxCss = prop.display.cssClass.findIndex(i => i.startsWith('cols-'));
            if (ndxCss >= 0) {
                prop.display.cssClass.splice(ndxCss, 1);
            }
            else {
                break;
            }
        } while (true);
    }
    retval.props.unshift({ prop: 'base', type: 'string', display: { cssClass: [PCSS + '-cell'] }, isReadOnly: true });
    return retval;
}
class CircularSlideRulerTransformers {
    static _getVisibleThetaRange(stage, pivot) {
        const rect = Utils.offsetRect(stage);
        const topleft = { x: rect.x, y: rect.y }, topright = { x: rect.x + rect.width, y: rect.y }, bottomright = { x: rect.x + rect.width, y: rect.y + rect.height }, bottomleft = { x: rect.x, y: rect.y + rect.height };
        const polygon = [topleft, topright, bottomright, bottomleft];
        if (Geometry.Utils.inPolygon(pivot, polygon)) {
            return [0, TWO_PI];
        }
        const versors = polygon.map(v => Geometry.LinearAlgebra.Vector.unit({ x: v.x - pivot.x, y: pivot.y - v.y }));
        const rays1 = versors.map(v => Geometry.LinearAlgebra.Vector.cross({ x: 0, y: -1 }, v));
        const angles1 = rays1.map(s => Math.asin(s));
        let range;
        const ySign = pivot.y < rect.y ? -1 : (pivot.y > (rect.y + rect.height) ? 1 : 0), xSign = pivot.x < rect.x ? -1 : (pivot.x > (rect.x + rect.width) ? 1 : 0);
        switch (xSign) {
            case -1:
                switch (ySign) {
                    case -1:
                        range = [angles1[1], angles1[3]];
                        break;
                    case 1:
                        range = [angles1[0], angles1[2]];
                        break;
                    default:
                        range = [angles1[0], angles1[3]];
                        break;
                }
                break;
            case 1:
                switch (ySign) {
                    case -1:
                        range = [angles1[2], angles1[0]];
                        break;
                    case 1:
                        range = [angles1[3], angles1[1]];
                        break;
                    default:
                        range = [angles1[2], angles1[1]];
                        break;
                }
                break;
            default:
                switch (ySign) {
                    case -1:
                        range = [angles1[1], angles1[0]];
                        break;
                    case 1:
                        range = [angles1[3], angles1[2]];
                        break;
                    default:
                        throw new Error('howcome?');
                }
                break;
        }
        let retval = range;
        return retval;
    }
    static circularSlideRulerTicks(type, stage, center, radius, pivot, zoom, offset = 0, base = 10, decimals = true) {
        base ||= 0;
        base = Math.max(1.0, base);
        const items = [];
        const inner = type === 'inner';
        while (offset < 0) {
            offset += TWO_PI;
        }
        offset %= TWO_PI;
        if (offset > Math.PI) {
            offset = -TWO_PI + offset;
        }
        const orderOfMag = Math.round(Math.log10(zoom) + Numbers.log(10, base) * .75);
        const factor = Math.pow(10, orderOfMag);
        const step = Math.pow(10, -orderOfMag);
        const lineWidth = .2;
        let pathData = '';
        const textColorEmpashis = Utils.Css.getVariableValue('--pacem-color-default');
        const color = inner ? Utils.Css.getVariableValue('--pacem-color-accent') : Utils.Css.getVariableValue('--pacem-color-primary');
        const tickSizeMax = 4;
        let start = 1, end = base;
        let seed = [[start, end]];
        if (center.y > (stage.viewbox.y + stage.viewbox.height)) {
            const range = CircularSlideRulerTransformers._getVisibleThetaRange(stage, pivot);
            const angleEnd0 = 0 + range[1] - offset, angleStart1 = TWO_PI + range[0] - offset;
            let end0 = CircularSlideRulerTransformers.circularSlideRulerValueFromRotation(base, -angleEnd0), start1 = CircularSlideRulerTransformers.circularSlideRulerValueFromRotation(base, -angleStart1);
            end0 = end0 - (end0 % step) + step;
            start1 = start1 - (start1 % step);
            end0 = end0.roundoff();
            start1 = start1.roundoff();
            end = Math.min(end0 * base, end).roundoff();
            start = Math.max(start1 / base, start).roundoff();
            end = end - (end % step) + step;
            start = start - (start % step);
            end = end.roundoff();
            start = start.roundoff();
            seed = [[start, end0], [start1, end]];
        }
        for (let tuple of seed) {
            for (let j = tuple[0]; j < tuple[1]; j += step) {
                j = j.roundoff();
                if (j < 1) {
                    continue;
                }
                if (j >= base) {
                    break;
                }
                const log = Numbers.log(j, base);
                const theta = TWO_PI * log + offset;
                const sin = Math.sin(theta), cos = Math.cos(theta);
                const x = center.x + radius * sin, y = center.y - radius * cos;
                let textColor = '';
                let tickWeight = tickSizeMax;
                let fontSize = 5;
                let label = true;
                const tickImportance = ((j / Math.ceil(base / 10)) % 1).roundoff();
                if (j !== 1 && tickImportance !== 0) {
                    if (tickImportance === .5) {
                        tickWeight *= .5;
                        fontSize *= .75;
                    }
                    else {
                        const jInt0 = BigInt(Math.round(j * factor));
                        if (jInt0 % 50n === 0n) {
                            tickWeight *= .5;
                            fontSize *= .5;
                        }
                        else {
                            tickWeight *= .1;
                            fontSize *= .4;
                            label = !j.isCloseTo(1) && (jInt0 % 25n === 0n);
                        }
                    }
                }
                else {
                    textColor = textColorEmpashis;
                }
                const xOff = tickWeight * sin, yOff = -tickWeight * cos;
                pathData += `M${x},${y} l${(inner ? -xOff : xOff)},${(inner ? -yOff : yOff)} `;
                if (label) {
                    let text = (decimals || base === 10 || j === 1) ? j.toString() : Mathematics.NumberTheory.Utils.rebaseFloat(j, 10, base).toString();
                    const textAnchor = inner ?
                        { x: (x - xOff) - fontSize * ((x - center.x) / radius), y: (y - yOff) + fontSize * (0.5 - ((y - center.y) / radius)) }
                        : { x: (x + xOff) + fontSize * ((x - center.x) / radius), y: (y + yOff) + fontSize * (0.5 + ((y - center.y) / radius)) };
                    const label = { anchor: textAnchor, text, fontSize, textAnchor: 'middle', color: textColor || color, inert: true };
                    items.push(label);
                }
            }
        }
        const ticks = { pathData, stroke: color, lineWidth, inert: true };
        items.push(ticks);
        return items;
    }
    static handleCircularSlideRotateStart(evt, center, radius, innerRotationViewModel, outerRotationViewModel) {
        const stage = evt.detail.element;
        const pt = { x: evt.pageX, y: evt.pageY };
        const stagePt = Matrix2D.multiply(pt, stage.transformMatrix);
        const distance = Point.distance(stagePt, center);
        if (distance > radius) {
            SET_VAL(stage, HIT_TARGET_ROTATION, OUTER_GEAR_TAG_ID);
        }
        else {
            SET_VAL(stage, HIT_TARGET_ROTATION, INNER_GEAR_TAG_ID);
        }
        SET_VAL(stage, INITIAL_INNER_ROTATION, innerRotationViewModel.model);
        SET_VAL(stage, INITIAL_OUTER_ROTATION, outerRotationViewModel.model);
    }
    static handleCircularSlideRotate(evt, innerRotationViewModel, outerRotationViewModel) {
        evt.preventDefault();
        const stage = evt.detail.element;
        const innerOffset = GET_VAL(stage, INITIAL_INNER_ROTATION, 0);
        const outerOffset = GET_VAL(stage, INITIAL_OUTER_ROTATION, 0);
        const rotation = evt.detail.rotation;
        switch (GET_VAL(evt.detail.element, HIT_TARGET_ROTATION)) {
            case OUTER_GEAR_TAG_ID:
                outerRotationViewModel.model = outerOffset + rotation;
            case INNER_GEAR_TAG_ID:
                innerRotationViewModel.model = innerOffset + rotation;
                break;
        }
    }
    static circularSlideRulerRatio(base, inner, outer) {
        return this.circularSlideRulerValueFromRotation(base, inner - outer);
    }
    static circularSlideRulerScientific(outvalue, base) {
        const baseRelative = Numbers.log(outvalue, base).roundoff();
        let baseString = base.toString();
        if (base === Math.PI) {
            baseString = '&pi;';
        }
        else if (base === Math.E) {
            baseString = 'e';
        }
        return `${baseString}<sup>${baseRelative}</sup>`;
    }
    static circularSlideRulerRebase(value, base, precision = 12) {
        const conv = Mathematics.NumberTheory.Utils.rebaseFloat(value, 10, base, precision);
        let baseString = base.toString();
        if (base === Math.PI) {
            baseString = '&pi;';
        }
        else if (base === Math.E) {
            baseString = 'e';
        }
        return `${conv}<sub>${baseString}</sub>`;
    }
    static circularSlideRulerValueFromRotation(base, rotation) {
        return Math.pow(base, -rotation / TWO_PI);
    }
    static circularSlideRulerRotationFromValue(base, value) {
        return -Numbers.log(value, base) * TWO_PI;
    }
    static circularSlideRulerRadius(radius, zoom) {
        return (radius * zoom).roundoff();
    }
    static circularSlideRulerCenter(center, radius, zoom) {
        const y = (center.y - (radius / zoom)).roundoff();
        const zoomedRadius = CircularSlideRulerTransformers.circularSlideRulerRadius(radius, zoom);
        return { x: center.x, y: (zoomedRadius + y).roundoff() };
    }
    static circularSlideRulerLocalToWorld(stage, zoomedCenter, matrix = stage.transformMatrix) {
        const stageToWorld = Matrix2D.invert(matrix);
        return Matrix2D.multiply(zoomedCenter, stageToWorld);
    }
    static handleCircularSlideRulerWheel(zoomViewModel, evt) {
        avoidHandler(evt);
        const zoomingOut = evt.deltaY > 0;
        const factor = .1, sign = zoomingOut ? -1 : 1, factorWSign = factor * sign, scale = 1 + factorWSign;
        const targetScale = zoomViewModel.model * scale;
        zoomViewModel.model = Math.min(CIRCULAR_SLIDE_MAX_ZOOM, Math.max(1.0, targetScale));
    }
    static tweenCircularSlideRuler(viewmodel, to, fallback = 0) {
        to ??= fallback;
        if (Number.isNaN(to) || !Number.isFinite(to)) {
            to = fallback;
        }
        const tw = new Animations.TweenService();
        tw.run(viewmodel.model, to, 500, 0, Animations.Easings.sineInOut, (_, value) => {
            viewmodel.model = value;
        });
    }
}
__decorate([
    Transformer()
], CircularSlideRulerTransformers, "circularSlideRulerTicks", null);
__decorate([
    Transformer()
], CircularSlideRulerTransformers, "handleCircularSlideRotateStart", null);
__decorate([
    Transformer()
], CircularSlideRulerTransformers, "handleCircularSlideRotate", null);
__decorate([
    Transformer()
], CircularSlideRulerTransformers, "circularSlideRulerRatio", null);
__decorate([
    Transformer()
], CircularSlideRulerTransformers, "circularSlideRulerScientific", null);
__decorate([
    Transformer()
], CircularSlideRulerTransformers, "circularSlideRulerRebase", null);
__decorate([
    Transformer()
], CircularSlideRulerTransformers, "circularSlideRulerValueFromRotation", null);
__decorate([
    Transformer()
], CircularSlideRulerTransformers, "circularSlideRulerRotationFromValue", null);
__decorate([
    Transformer()
], CircularSlideRulerTransformers, "circularSlideRulerRadius", null);
__decorate([
    Transformer()
], CircularSlideRulerTransformers, "circularSlideRulerCenter", null);
__decorate([
    Transformer()
], CircularSlideRulerTransformers, "circularSlideRulerLocalToWorld", null);
__decorate([
    Transformer()
], CircularSlideRulerTransformers, "handleCircularSlideRulerWheel", null);
__decorate([
    Transformer()
], CircularSlideRulerTransformers, "tweenCircularSlideRuler", null);
